// src/theme/themeConfig.ts
import { createTheme, Theme } from '@mui/material/styles';
import { createGlobalOverrides } from './globalOverrides';
import { colors } from './theme'; // SINGLE SOURCE OF TRUTH - Import ALL colors

// Enhanced typography for accessibility (no colors here)
const accessibleTypography = {
  fontSize: 18,
  fontFamily: ['Segoe UI', 'Roboto', 'Arial', 'sans-serif'].join(','),
  fontWeightLight: 400,
  fontWeightRegular: 500,
  fontWeightMedium: 600,
  fontWeightBold: 700,

  h1: {
    fontSize: '3.5rem',
    fontWeight: 900,
    lineHeight: 1.2,
    letterSpacing: '0.02em',
  },
  h2: {
    fontSize: '2.8rem',
    fontWeight: 800,
    lineHeight: 1.3,
    letterSpacing: '0.01em',
  },
  h3: {
    fontSize: '2.2rem',
    fontWeight: 700,
    lineHeight: 1.4,
  },
  h4: {
    fontSize: '1.8rem',
    fontWeight: 700,
    lineHeight: 1.4,
  },
  h5: {
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1.5,
  },
  h6: {
    fontSize: '1.3rem',
    fontWeight: 600,
    lineHeight: 1.5,
  },
  body1: {
    fontSize: '1.2rem',
    fontWeight: 500,
    lineHeight: 1.6,
  },
  body2: {
    fontSize: '1.1rem',
    fontWeight: 500,
    lineHeight: 1.6,
  },
  button: {
    fontSize: '1.1rem',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },
};

// Function to create theme based on mode - USES COLORS FROM THEME.TS ONLY
export const createAppTheme = (mode: 'light' | 'dark'): Theme => {
  // For dark mode, we might want to adjust some colors for better contrast
  // But we base everything on our single source colors from theme.ts

  const isDark = mode === 'dark';

  // Dark mode color adjustments (only if needed for visibility)
  const primaryColors = isDark
    ? {
        light: '#64b5f6', // Lighter blue for dark theme visibility
        main: colors.primary.main, // Keep our main color
        dark: colors.primary.dark, // Keep our dark color
      }
    : colors.primary;

  const secondaryColors = isDark
    ? {
        light: '#ef5350', // Brighter for dark theme visibility
        main: colors.secondary.main, // Keep our burgundy
        dark: colors.secondary.dark, // Keep our dark burgundy
      }
    : colors.secondary;

  const tertiaryColors = isDark
    ? {
        light: '#bdbdbd', // Lighter grey for dark theme
        main: colors.tertiary.main, // Keep our grey
        dark: colors.tertiary.dark, // Keep our dark grey
      }
    : colors.tertiary;

  const backgroundColors = isDark
    ? {
        default: '#121212', // Material Design dark default
        paper: '#1e1e1e', // Dark theme paper
      }
    : {
        default: colors.background.light, // Use our light background
        paper: '#ffffff',
      };

  const textColors = isDark
    ? {
        primary: '#ffffff', // White text for dark theme
        secondary: '#e0e0e0', // Light grey for dark theme
        disabled: '#9e9e9e', // Standard disabled for dark
      }
    : {
        primary: colors.text.dark, // Use our dark text
        secondary: colors.tertiary.main, // Use our tertiary for secondary text
        disabled: colors.tertiary.light, // Use our light tertiary for disabled
      };

  // Create base theme using our single source colors
  const baseTheme = createTheme({
    palette: {
      mode,
      primary: {
        light: primaryColors.light,
        main: primaryColors.main,
        dark: primaryColors.dark,
        contrastText: colors.text.light, // Always use our contrast text
      },
      secondary: {
        light: secondaryColors.light,
        main: secondaryColors.main,
        dark: secondaryColors.dark,
        contrastText: colors.text.light,
      },
      tertiary: {
        light: tertiaryColors.light,
        main: tertiaryColors.main,
        dark: tertiaryColors.dark,
        contrastText: colors.text.light,
      },
      error: {
        main: colors.error.main, // Use our error colors
        dark: colors.error.dark,
        light: colors.error.light,
        contrastText: colors.text.light,
      },
      warning: {
        main: colors.warning.main, // Use our warning colors
        dark: colors.warning.dark,
        light: colors.warning.light,
        contrastText: colors.text.dark,
      },
      success: {
        main: colors.success.main, // Use our success colors
        dark: colors.success.dark,
        light: colors.success.light,
        contrastText: colors.text.light,
      },
      info: {
        main: colors.info.main, // Use our info colors
        dark: colors.info.dark,
        light: colors.info.light,
        contrastText: colors.text.light,
      },
      background: backgroundColors,
      text: textColors,
      grey: colors.grey, // Use our grey scale
      alternatingRows: colors.alternatingRows, // Use our alternating rows
    },

    typography: accessibleTypography,

    shape: {
      borderRadius: 8,
    },

    components: createGlobalOverrides(
      createTheme({
        palette: {
          mode,
          primary: {
            light: primaryColors.light,
            main: primaryColors.main,
            dark: primaryColors.dark,
            contrastText: colors.text.light,
          },
          secondary: {
            light: secondaryColors.light,
            main: secondaryColors.main,
            dark: secondaryColors.dark,
            contrastText: colors.text.light,
          },
          tertiary: {
            light: tertiaryColors.light,
            main: tertiaryColors.main,
            dark: tertiaryColors.dark,
            contrastText: colors.text.light,
          },
          error: {
            main: colors.error.main,
            dark: colors.error.dark,
            light: colors.error.light,
            contrastText: colors.text.light,
          },
          warning: {
            main: colors.warning.main,
            dark: colors.warning.dark,
            light: colors.warning.light,
            contrastText: colors.text.dark,
          },
          success: {
            main: colors.success.main,
            dark: colors.success.dark,
            light: colors.success.light,
            contrastText: colors.text.light,
          },
          info: {
            main: colors.info.main,
            dark: colors.info.dark,
            light: colors.info.light,
            contrastText: colors.text.light,
          },
          background: backgroundColors,
          text: textColors,
          grey: colors.grey,
          alternatingRows: colors.alternatingRows,
        },
        typography: accessibleTypography,
        shape: { borderRadius: 8 },
      })
    ),
  });

  return baseTheme;
};

// Helper function - returns mode-aware color information
// For theme switching to work, components should use theme.palette instead of this function
// This is kept for backwards compatibility and for accessing gradients/shadows/accessibility
export const getAppThemeColors = (mode: 'light' | 'dark') => {
  // Return our colors object which includes gradients, shadows, and accessibility
  // These typically don't change between light/dark modes
  // For theme-aware colors, use theme.palette.* in your components
  return {
    ...colors,
    // Note: Most components should use theme.palette for theme-aware colors
    // This function primarily provides gradients, shadows, and accessibility configs
  };
};

// Accessibility utilities for global use (no colors here)
export const appAccessibilityConfig = {
  focusOutline: 'none',
  focusOffset: '2px',
  minTouchTarget: '44px',
  highContrastBorder: '2px solid currentColor',
  transitions: {
    standard: 'all 0.3s ease',
    fast: 'all 0.15s ease',
    slow: 'all 0.5s ease',
  },
};
